/**
 * OptionComposerPage — Phase 2
 * Multi-leg option strategy composer. Local state only; no backend calls.
 * Receives optional `template` via router location.state.
 */
import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Copy, Trash2, Plus, Minus, Rocket, BarChart2, ChevronDown,
} from 'lucide-react';
import {
  ComposedChart, Line, ReferenceLine, XAxis, YAxis,
  Tooltip as ReTooltip, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';
import { toast } from '../../store';
import { ROUTES } from '../../routes/constants';
import { optionsApi } from '../../api/client';

// ─── Constants ────────────────────────────────────────────────────────────────

const UNDERLYINGS = [
  { value: 'NIFTY',     label: 'NIFTY',     spot: 23450, lotSize: 75 },
  { value: 'BANKNIFTY', label: 'BANKNIFTY', spot: 51200, lotSize: 15 },
  { value: 'SENSEX',    label: 'SENSEX',    spot: 77200, lotSize: 10 },
  { value: 'FINNIFTY',  label: 'FINNIFTY',  spot: 23800, lotSize: 40 },
];

const EXPIRY_OPTIONS = [
  { value: 'current_week',  label: 'Current Week' },
  { value: 'next_week',     label: 'Next Week' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'next_month',    label: 'Next Month' },
];

const FAMILY_BADGE = {
  bullish:    'bg-bull/10 text-bull border-bull/20',
  bearish:    'bg-bear/10 text-bear border-bear/20',
  neutral:    'bg-info/10 text-info border-info/20',
  volatility: 'bg-amber/10 text-amber border-amber/20',
  custom:     'bg-bg-elevated text-txt-muted border-bg-border',
};

// ─── Template Pre-fill ────────────────────────────────────────────────────────

function makeLeg(overrides = {}) {
  return {
    id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
    action: 'BUY',
    optionType: 'CE',
    strikeMode: 'ATM',
    offset: 1,
    premiumTarget: '',
    lots: 1,
    ratio: 1,
    expiry: null, // null = inherit global expiry
    ...overrides,
  };
}

// Template leg definitions — keyed by template.id from OptionsHomePage
const TEMPLATE_LEGS = {
  'bull-call-spread': [
    { action: 'BUY',  optionType: 'CE', strikeMode: 'ATM' },
    { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1 },
  ],
  'bull-put-spread': [
    { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1 },
    { action: 'BUY',  optionType: 'PE', strikeMode: 'OTM', offset: 2 },
  ],
  'bear-call-spread': [
    { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1 },
    { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 2 },
  ],
  'bear-put-spread': [
    { action: 'BUY',  optionType: 'PE', strikeMode: 'ATM' },
    { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1 },
  ],
  'long-straddle': [
    { action: 'BUY', optionType: 'CE', strikeMode: 'ATM' },
    { action: 'BUY', optionType: 'PE', strikeMode: 'ATM' },
  ],
  'short-straddle': [
    { action: 'SELL', optionType: 'CE', strikeMode: 'ATM' },
    { action: 'SELL', optionType: 'PE', strikeMode: 'ATM' },
  ],
  'long-strangle': [
    { action: 'BUY', optionType: 'CE', strikeMode: 'OTM', offset: 1 },
    { action: 'BUY', optionType: 'PE', strikeMode: 'OTM', offset: 1 },
  ],
  'short-strangle': [
    { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1 },
    { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1 },
  ],
  'iron-condor': [
    { action: 'BUY',  optionType: 'PE', strikeMode: 'OTM', offset: 2 },
    { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1 },
    { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1 },
    { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 2 },
  ],
  'iron-fly': [
    { action: 'BUY',  optionType: 'PE', strikeMode: 'OTM', offset: 1 },
    { action: 'SELL', optionType: 'PE', strikeMode: 'ATM' },
    { action: 'SELL', optionType: 'CE', strikeMode: 'ATM' },
    { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 1 },
  ],
  'butterfly': [
    { action: 'BUY',  optionType: 'CE', strikeMode: 'ITM', offset: 1 },
    { action: 'SELL', optionType: 'CE', strikeMode: 'ATM', lots: 2 },
    { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 1 },
  ],
  'calendar': [
    { action: 'SELL', optionType: 'CE', strikeMode: 'ATM', expiry: 'current_week' },
    { action: 'BUY',  optionType: 'CE', strikeMode: 'ATM', expiry: 'next_week' },
  ],
  'jade-lizard': [
    { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1 },
    { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1 },
    { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 2 },
  ],
};

function buildLegsFromTemplate(template) {
  const defs = template ? (TEMPLATE_LEGS[template.id] ?? []) : [];
  if (defs.length === 0) return [makeLeg()];
  return defs.map((d) => makeLeg(d));
}

// ─── Payoff Calculation Engine (frontend-only, temporary approximation) ───────
// NOTE: Uses expiry-style intrinsic-value model. No Black-Scholes or Greeks.
// Replace with backend payoff API when available.

/** Strike step (points) per underlying. */
const STRIKE_STEP = { NIFTY: 50, BANKNIFTY: 100, SENSEX: 100, FINNIFTY: 50 };

/**
 * Resolve effective strike for a leg.
 * OTM CE = above spot, ITM CE = below spot; reversed for PE.
 */
function resolveStrike(leg, spot, step) {
  const atm = Math.round(spot / step) * step;
  const off = leg.offset ?? 1;
  if (leg.strikeMode === 'ITM') return leg.optionType === 'CE' ? atm - off * step : atm + off * step;
  if (leg.strikeMode === 'OTM') return leg.optionType === 'CE' ? atm + off * step : atm - off * step;
  return atm; // ATM and premium mode both map to ATM for chart
}

/**
 * Rough premium estimate — intrinsic + heuristic time value.
 * Premium mode with a valid numeric target uses that directly.
 */
function estimatePremium(strike, spot, optionType, strikeMode, premiumTarget) {
  if (strikeMode === 'premium') {
    const pt = Number(premiumTarget);
    if (pt > 0) return pt;
  }
  const intrinsic  = optionType === 'CE' ? Math.max(0, spot - strike) : Math.max(0, strike - spot);
  const timeValue  = spot * 0.01 * Math.exp(-Math.abs(spot - strike) / (spot * 0.03));
  return Math.max(0.5, intrinsic + timeValue);
}

/**
 * Compute 41-point payoff series and derive summary metrics from it.
 * Returns null when no valid legs are present.
 */
function computePayoffSeries(legs, spot, lotSize, underlying) {
  const step  = STRIKE_STEP[underlying] ?? 50;
  const valid = legs.filter(l => l.action && l.optionType && l.strikeMode);
  if (valid.length === 0) return null;

  const resolved = valid.map(leg => {
    const strike  = resolveStrike(leg, spot, step);
    const premium = estimatePremium(strike, spot, leg.optionType, leg.strikeMode, leg.premiumTarget);
    return { ...leg, strike, premium };
  });

  // Net premium: negative = debit, positive = credit
  const netPremiumRaw = resolved.reduce((sum, l) => {
    const sign = l.action === 'BUY' ? -1 : 1;
    return sum + sign * l.premium * l.lots * (l.ratio || 1) * lotSize;
  }, 0);

  // Price range: 41 points centered on spot
  const maxOff  = Math.max(...resolved.map(l => l.offset ?? 1), 2);
  const half    = Math.max(step * (maxOff + 3) * 2, spot * 0.08);
  const N       = 41;

  const series = Array.from({ length: N }, (_, i) => {
    const price = Math.round((spot - half) + (i / (N - 1)) * half * 2);
    const pnl   = Math.round(resolved.reduce((sum, l) => {
      const intrinsic = l.optionType === 'CE'
        ? Math.max(0, price - l.strike)
        : Math.max(0, l.strike - price);
      const legPnl = l.action === 'BUY'
        ? (intrinsic - l.premium) * l.lots * (l.ratio || 1) * lotSize
        : (l.premium - intrinsic) * l.lots * (l.ratio || 1) * lotSize;
      return sum + legPnl;
    }, 0));
    return { price, pnl };
  });

  const pnlArr = series.map(d => d.pnl);
  const maxPnl = Math.max(...pnlArr);
  const minPnl = Math.min(...pnlArr);

  // "Potentially Unlimited" when payoff is still expanding at the edge
  const unlimitedProfit = series[N - 1].pnl > maxPnl * 0.92 && series[N - 1].pnl > 0;
  const unlimitedLoss   = series[0].pnl < minPnl * 0.92 && series[0].pnl < 0;

  // Zero crossings → breakeven prices via linear interpolation
  const bePoints = [];
  for (let i = 1; i < series.length; i++) {
    const p = series[i - 1].pnl, c = series[i].pnl;
    if ((p < 0 && c >= 0) || (p > 0 && c <= 0)) {
      const t = Math.abs(p) / (Math.abs(p) + Math.abs(c) || 1);
      bePoints.push(Math.round(series[i - 1].price + t * (series[i].price - series[i - 1].price)));
    }
  }

  const fmtInr = v => `₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;

  return {
    series,
    summary: {
      n:               valid.length,
      maxProfit:       unlimitedProfit ? 'Potentially Unlimited' : fmtInr(maxPnl),
      maxLoss:         unlimitedLoss   ? 'Potentially Unlimited' : fmtInr(minPnl),
      breakeven:       bePoints.length ? bePoints.map(b => b.toLocaleString('en-IN')).join(' / ') : '—',
      netPremium:      netPremiumRaw < -0.5
                         ? `Net Debit ${fmtInr(netPremiumRaw)}`
                         : netPremiumRaw > 0.5
                         ? `Net Credit ${fmtInr(netPremiumRaw)}`
                         : 'Neutral',
      netPremiumLabel: netPremiumRaw < -0.5 ? 'Net Debit' : netPremiumRaw > 0.5 ? 'Net Credit' : 'Neutral',
    },
    breakevenPrices: bePoints,
    spotPrice: spot,
  };
}

function usePayoffData(legs, spot, lotSize, underlying) {
  return useMemo(
    () => computePayoffSeries(legs, spot, lotSize, underlying),
    [legs, spot, lotSize, underlying],
  );
}

// ─── Payoff Chart ─────────────────────────────────────────────────────────────

// Read a CSS variable (stored as "R G B") and format it for Recharts.
// Falls back to dark-mode token values when window/document is unavailable (SSR/prerender).
function cssVar(name, fallbackTriple, alpha) {
  const raw = (typeof window !== 'undefined' && document?.documentElement)
    ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    : '';
  const rgb = raw || fallbackTriple;
  return alpha != null ? `rgb(${rgb} / ${alpha})` : `rgb(${rgb})`;
}

function PayoffChart({ series, spotPrice, breakevenPrices }) {
  if (!series || series.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 p-5 text-center">
        <BarChart2 size={26} className="text-txt-muted/20" />
        <span className="text-xs font-mono text-txt-muted">Add legs to visualize payoff</span>
      </div>
    );
  }

  const pnlArr  = series.map(d => d.pnl);
  const maxPnl  = Math.max(...pnlArr);
  const minPnl  = Math.min(...pnlArr);
  const pad     = Math.max(Math.abs(maxPnl), Math.abs(minPnl)) * 0.12;
  const yDomain = [minPnl - pad, maxPnl + pad];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const pnl = payload[0].value;
    return (
      <div className="bg-bg-elevated border border-bg-border/80 rounded px-2.5 py-1.5 text-2xs font-mono shadow-lg">
        <div className="text-txt-muted">
          Price <span className="text-txt-primary tabular-nums">{Number(label).toLocaleString('en-IN')}</span>
        </div>
        <div className={pnl >= 0 ? 'text-bull' : 'text-bear'}>
          P&L <span className="tabular-nums">{pnl >= 0 ? '+' : ''}{Math.round(pnl).toLocaleString('en-IN')}</span>
        </div>
      </div>
    );
  };

  const tickFmt  = v => Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v);
  const priceFmt = v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={series} margin={{ top: 12, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={cssVar('--bg-border', '35 44 64')} vertical={false} />
        <XAxis
          dataKey="price"
          tickFormatter={priceFmt}
          tick={{ fill: cssVar('--txt-muted', '120 132 158', 0.6), fontSize: 9, fontFamily: 'monospace' }}
          axisLine={{ stroke: cssVar('--bg-border', '35 44 64') }}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tickFormatter={tickFmt}
          tick={{ fill: cssVar('--txt-muted', '120 132 158', 0.6), fontSize: 9, fontFamily: 'monospace' }}
          axisLine={false}
          tickLine={false}
          domain={yDomain}
          width={52}
        />
        <ReTooltip content={<CustomTooltip />} cursor={{ stroke: cssVar('--bg-border', '35 44 64', 0.5), strokeWidth: 1 }} />

        {/* Zero line */}
        <ReferenceLine y={0} stroke={cssVar('--txt-muted', '120 132 158', 0.3)} strokeWidth={1} />

        {/* Spot price marker */}
        <ReferenceLine
          x={spotPrice}
          stroke={cssVar('--amber', '232 163 61', 0.5)}
          strokeDasharray="4 3"
          label={{ value: 'Spot', position: 'insideTopRight', fill: cssVar('--amber', '232 163 61', 0.75), fontSize: 9, fontFamily: 'monospace' }}
        />

        {/* Breakeven markers */}
        {breakevenPrices.map((be, i) => (
          <ReferenceLine
            key={i}
            x={be}
            stroke={cssVar('--bull', '47 191 143', 0.4)}
            strokeDasharray="2 4"
            label={{ value: 'BE', position: 'insideTopLeft', fill: cssVar('--bull', '47 191 143', 0.7), fontSize: 8, fontFamily: 'monospace' }}
          />
        ))}

        {/* Single clean payoff line */}
        <Line
          type="monotone"
          dataKey="pnl"
          stroke={cssVar('--amber', '232 163 61')}
          strokeWidth={1.75}
          dot={false}
          activeDot={{ r: 3, fill: cssVar('--amber', '232 163 61'), strokeWidth: 0 }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, valueClass }) {
  return (
    <div className="bg-bg-surface/60 border border-bg-border/70 rounded-lg px-3 py-2.5 flex flex-col gap-0.5">
      <div className="field-label">{label}</div>
      <div className={cn('text-sm font-semibold font-mono', valueClass || 'text-txt-primary')}>
        {value}
      </div>
    </div>
  );
}

// ─── Leg Row ──────────────────────────────────────────────────────────────────

function LegRow({ leg, index, totalLegs, globalExpiry, onUpdate, onDuplicate, onRemove }) {
  const isBuy       = leg.action === 'BUY';
  const isCE        = leg.optionType === 'CE';
  const effectiveExp = leg.expiry || globalExpiry;
  const showOffset  = leg.strikeMode === 'ITM' || leg.strikeMode === 'OTM';
  const showPremium = leg.strikeMode === 'premium';

  return (
    <div
      className="grid items-center gap-x-1.5 px-3 py-2 border-b border-bg-border/40 hover:bg-bg-hover/25 transition-colors"
      style={{ gridTemplateColumns: '24px 80px 60px 110px 1fr 84px 52px 52px' }}
    >
      {/* Index */}
      <span className="text-2xs font-mono text-txt-muted text-center select-none">{index + 1}</span>

      {/* Action toggle */}
      <button
        type="button"
        onClick={() => onUpdate({ action: isBuy ? 'SELL' : 'BUY' })}
        className={cn(
          'text-2xs font-mono font-semibold py-1.5 rounded border transition-colors w-full',
          isBuy
            ? 'bg-bull/10 text-bull border-bull/25 hover:bg-bull/20'
            : 'bg-bear/10 text-bear border-bear/25 hover:bg-bear/20'
        )}
      >
        {isBuy ? 'BUY' : 'SELL'}
      </button>

      {/* CE / PE toggle */}
      <button
        type="button"
        onClick={() => onUpdate({ optionType: isCE ? 'PE' : 'CE' })}
        className={cn(
          'text-2xs font-mono font-semibold py-1.5 rounded border transition-colors w-full',
          isCE
            ? 'bg-info/10 text-info border-info/25 hover:bg-info/20'
            : 'bg-warn/10 text-warn border-warn/25 hover:bg-warn/20'
        )}
      >
        {isCE ? 'CE' : 'PE'}
      </button>

      {/* Expiry */}
      <div className="relative">
        <select
          value={effectiveExp}
          onChange={(e) => onUpdate({ expiry: e.target.value })}
          className={cn(
            'input-base h-8 appearance-none pr-6 text-2xs cursor-pointer py-0',
            leg.expiry ? 'text-amber' : ''
          )}
        >
          {EXPIRY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown size={9} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-txt-muted pointer-events-none" />
      </div>

      {/* Strike — mode tabs + conditional input */}
      <div className="flex items-center gap-1 min-w-0">
        {['ATM', 'ITM', 'OTM', 'Premium'].map((mode) => {
          const modeKey = mode === 'Premium' ? 'premium' : mode;
          const isActive = leg.strikeMode === modeKey;
          return (
            <button
              key={mode}
              type="button"
              onClick={() => onUpdate({ strikeMode: modeKey })}
              className={cn(
                'text-2xs font-mono px-1.5 py-0.5 rounded border shrink-0 transition-colors',
                isActive
                  ? 'bg-amber/10 text-amber border-amber/35'
                  : 'border-bg-border text-txt-muted hover:text-txt-secondary hover:border-bg-hover'
              )}
            >
              {mode}
            </button>
          );
        })}
        {showOffset && (
          <select
            value={String(leg.offset)}
            onChange={(e) => onUpdate({ offset: Number(e.target.value) })}
            className="input-base h-7 w-12 text-2xs py-0 px-1.5 cursor-pointer shrink-0"
          >
            <option value="1">1×</option>
            <option value="2">2×</option>
          </select>
        )}
        {showPremium && (
          <input
            type="number"
            min="0"
            step="0.5"
            value={leg.premiumTarget}
            onChange={(e) => onUpdate({ premiumTarget: e.target.value })}
            placeholder="₹"
            className="input-base h-7 w-16 text-2xs py-0 px-2 shrink-0"
          />
        )}
      </div>

      {/* Lots stepper */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onUpdate({ lots: Math.max(1, leg.lots - 1) })}
          className="w-6 h-6 rounded border border-bg-border text-txt-muted hover:bg-bg-hover hover:text-txt-secondary flex items-center justify-center shrink-0 transition-colors"
        >
          <Minus size={9} />
        </button>
        <span className="font-mono text-xs text-txt-primary w-5 text-center tabular-nums select-none">
          {leg.lots}
        </span>
        <button
          type="button"
          onClick={() => onUpdate({ lots: leg.lots + 1 })}
          className="w-6 h-6 rounded border border-bg-border text-txt-muted hover:bg-bg-hover hover:text-txt-secondary flex items-center justify-center shrink-0 transition-colors"
        >
          <Plus size={9} />
        </button>
      </div>

      {/* Ratio */}
      <input
        type="number"
        min="1"
        value={leg.ratio}
        onChange={(e) => onUpdate({ ratio: Math.max(1, Number(e.target.value) || 1) })}
        className="input-base h-7 text-2xs py-0 text-center"
      />

      {/* Row actions */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          title="Duplicate leg"
          onClick={onDuplicate}
          className="w-6 h-6 rounded border border-bg-border text-txt-muted hover:bg-bg-hover hover:text-txt-secondary flex items-center justify-center transition-colors"
        >
          <Copy size={10} />
        </button>
        {totalLegs > 1 && (
          <button
            type="button"
            title="Remove leg"
            onClick={onRemove}
            className="w-6 h-6 rounded border border-bear/20 text-bear/50 hover:bg-bear/10 hover:text-bear flex items-center justify-center transition-colors"
          >
            <Trash2 size={10} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OptionComposerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { template = null, mode, strategy: editStrategy } = location.state ?? {};
  const isEditMode = mode === 'edit' && editStrategy != null;

  // Header state — seeded from editStrategy when editing, otherwise defaults
  const [underlyingKey, setUnderlyingKey] = useState(isEditMode ? editStrategy.underlying    : 'NIFTY');
  const [globalExpiry,  setGlobalExpiry]  = useState(isEditMode ? editStrategy.expiry         : 'current_week');
  const [lotSize,       setLotSize]       = useState(isEditMode ? editStrategy.lotSize         : 75);

  const underlyingInfo = UNDERLYINGS.find(u => u.value === underlyingKey) ?? UNDERLYINGS[0];

  function handleUnderlyingChange(val) {
    const info = UNDERLYINGS.find(u => u.value === val);
    if (info) { setUnderlyingKey(val); setLotSize(info.lotSize); }
  }

  // Strategy name
  const [strategyName, setStrategyName] = useState(
    isEditMode ? editStrategy.name : (template?.name ?? 'Custom Option Strategy')
  );

  // Legs — seeded from editStrategy legs (with stable ids) when editing
  const [legs, setLegs] = useState(() => {
    if (isEditMode) {
      return editStrategy.legs.map((l) => ({
        ...l,
        id: l.id ?? `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
      }));
    }
    return buildLegsFromTemplate(template);
  });

  // ── Slug + strategy builder ──────────────────────────────────────────────
  function slugify(name) {
    return name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'custom-strategy';
  }

  function buildStrategy() {
    const id = isEditMode
      ? editStrategy.id
      : (template?.id ?? slugify(strategyName));
    return {
      id,
      name: strategyName || 'Custom Option Strategy',
      family: editStrategy?.family ?? template?.family ?? 'custom',
      underlying: underlyingKey,
      expiry: globalExpiry,
      lotSize,
      createdAt: editStrategy?.createdAt ?? new Date().toISOString().slice(0, 10),
      status: 'saved',
      legs: legs.map((leg) => ({ ...leg })),
    };
  }

  function updateLeg(id, patch) {
    setLegs(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  }
  function addLeg() {
    setLegs(prev => [...prev, makeLeg()]);
  }
  function duplicateLeg(id) {
    setLegs(prev => {
      const idx = prev.findIndex(l => l.id === id);
      if (idx < 0) return prev;
      const clone = { ...prev[idx], id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}` };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next;
    });
  }
  function removeLeg(id) {
    setLegs(prev => prev.filter(l => l.id !== id));
  }

  // Right panel tab
  const [activeTab, setActiveTab] = useState('payoff');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);

  // Real payoff data — computed from legs reactively
  const payoff  = usePayoffData(legs, underlyingInfo.spot, lotSize, underlyingKey);
  const summary = payoff?.summary ?? {
    n: legs.length, netPremiumLabel: null,
    maxProfit: '—', maxLoss: '—', breakeven: '—', netPremium: '—',
  };

  // Compact summary line: "N legs · Net Debit · bullish"
  const summaryLine = [
    `${summary.n} leg${summary.n !== 1 ? 's' : ''}`,
    summary.n > 0 && summary.netPremiumLabel ? summary.netPremiumLabel : null,
    template?.family ?? null,
  ].filter(Boolean).join(' · ');

  const netPremiumClass =
    summary.netPremiumLabel === 'Net Debit'  ? 'text-bear' :
    summary.netPremiumLabel === 'Net Credit' ? 'text-bull' : 'text-txt-muted';

  // ── Save / Deploy handlers ───────────────────────────────────────────────
  async function handleSaveStrategy() {
    if (legs.length === 0) {
      toast.warn('Add at least one leg before saving');
      return;
    }
    const localStrategy = buildStrategy();
    setIsSaving(true);
    try {
      const saved = await optionsApi.save(localStrategy, { preferUpdate: isEditMode });
      toast.success(`"${saved.name}" saved`);
      navigate(ROUTES.options(saved.id), { state: { strategy: saved } });
    } catch (err) {
      toast.warn(`Save failed on backend (${err.message}). Using local preview.`);
      navigate(ROUTES.options(localStrategy.id), { state: { strategy: localStrategy } });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeploy() {
    if (legs.length === 0) {
      toast.warn('Add at least one leg before deploying');
      return;
    }
    const localStrategy = buildStrategy();
    setIsDeploying(true);
    try {
      const saved = await optionsApi.save(localStrategy, { preferUpdate: isEditMode });
      navigate(ROUTES.optionsDeploy(saved.id), { state: { strategy: saved } });
    } catch (err) {
      toast.error(`Failed to save strategy: ${err.message}`);
    } finally {
      setIsDeploying(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">

      {/* ── 1. Trading Header ── */}
      <div className="panel px-4 py-2.5 flex items-center gap-4 flex-wrap">

        {/* Underlying */}
        <div className="flex items-center gap-2">
          <span className="field-label shrink-0">Underlying</span>
          <div className="relative">
            <select
              value={underlyingKey}
              onChange={(e) => handleUnderlyingChange(e.target.value)}
              className="input-base h-8 appearance-none pr-7 text-xs cursor-pointer py-0 font-semibold font-mono w-32"
            >
              {UNDERLYINGS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
            <ChevronDown size={9} className="absolute right-2 top-1/2 -translate-y-1/2 text-txt-muted pointer-events-none" />
          </div>
        </div>

        {/* Spot price (mock) */}
        <div className="flex items-center gap-1.5">
          <span className="field-label">Spot</span>
          <span className="font-mono text-xs font-semibold text-txt-primary tabular-nums">
            {underlyingInfo.spot.toLocaleString('en-IN')}
          </span>
          <span className="text-2xs font-mono text-txt-muted">(mock)</span>
        </div>

        <div className="w-px h-4 bg-bg-border hidden sm:block" />

        {/* Global expiry */}
        <div className="flex items-center gap-2">
          <span className="field-label shrink-0">Expiry</span>
          <div className="relative">
            <select
              value={globalExpiry}
              onChange={(e) => setGlobalExpiry(e.target.value)}
              className="input-base h-8 appearance-none pr-6 text-xs cursor-pointer py-0 w-36"
            >
              {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown size={9} className="absolute right-2 top-1/2 -translate-y-1/2 text-txt-muted pointer-events-none" />
          </div>
        </div>

        {/* Lot size */}
        <div className="flex items-center gap-2">
          <span className="field-label shrink-0">Lot Size</span>
          <input
            type="number"
            min="1"
            value={lotSize}
            onChange={(e) => setLotSize(Math.max(1, Number(e.target.value) || 1))}
            className="input-base h-8 w-16 text-xs py-0 text-center font-mono"
          />
        </div>

        <div className="w-px h-4 bg-bg-border hidden sm:block" />

        {/* IV placeholder */}
        <div className="flex items-center gap-1.5">
          <span className="field-label">IV</span>
          <span className="font-mono text-xs text-warn tabular-nums">~14.2%</span>
          <span className="text-2xs font-mono text-txt-muted hidden md:inline">(placeholder)</span>
        </div>
      </div>

      {/* ── 2. Strategy Name + Back ── */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(ROUTES.OPTIONS)}
          className="btn-ghost p-1.5 shrink-0"
          title="Back to options"
        >
          <ArrowLeft size={15} />
        </button>
        {isEditMode && (
          <span className="badge border text-2xs font-mono shrink-0 bg-amber/10 text-amber border-amber/25">
            Editing
          </span>
        )}
        <input
          type="text"
          value={strategyName}
          onChange={(e) => setStrategyName(e.target.value)}
          className="input-base h-9 text-sm font-display font-semibold max-w-xs"
          placeholder="Strategy name"
        />
        {(template?.family || editStrategy?.family) && (
          <span className={cn('badge border text-2xs font-mono shrink-0', FAMILY_BADGE[template?.family ?? editStrategy?.family] ?? FAMILY_BADGE.custom)}>
            {template?.family ?? editStrategy?.family}
          </span>
        )}
      </div>

      {/* ── 3. Split Panels ── */}
      <div className="flex flex-col lg:flex-row gap-3">

        {/* LEFT — Leg Builder */}
        <div className="flex-[11] min-w-0 panel flex flex-col">

          {/* Panel header */}
          <div className="px-4 py-2.5 border-b border-bg-border/70 flex items-center justify-between shrink-0 flex-wrap gap-2">
            <div className="flex items-center gap-2.5">
              <span className="section-title">Legs</span>
              <span className="text-2xs font-mono text-txt-muted">{summaryLine}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xs font-mono text-txt-muted hidden sm:inline">
                Order qty = lots × lot size
              </span>
              <button type="button" onClick={addLeg} className="btn-secondary text-xs">
                <Plus size={11} />
                Add Leg
              </button>
            </div>
          </div>

          {/* Table column headers */}
          <div
            className="grid px-3 py-1.5 border-b border-bg-border/50 bg-bg-surface/20 shrink-0"
            style={{ gridTemplateColumns: '24px 80px 60px 110px 1fr 84px 52px 52px' }}
          >
            {['#', 'Action', 'Type', 'Expiry', 'Strike', 'Lots', 'Ratio', ''].map((h, i) => (
              <span key={i} className="field-label text-center">{h}</span>
            ))}
          </div>

          {/* Leg rows */}
          <div className="flex-1 overflow-x-auto overflow-y-auto min-h-[180px]">
            {legs.length === 0 ? (
              <div className="py-12 flex flex-col items-center gap-2 text-txt-muted">
                <BarChart2 size={22} className="opacity-20" />
                <span className="text-2xs font-mono">No legs — click Add Leg to start.</span>
              </div>
            ) : (
              legs.map((leg, idx) => (
                <LegRow
                  key={leg.id}
                  leg={leg}
                  index={idx}
                  totalLegs={legs.length}
                  globalExpiry={globalExpiry}
                  onUpdate={(patch) => updateLeg(leg.id, patch)}
                  onDuplicate={() => duplicateLeg(leg.id)}
                  onRemove={() => removeLeg(leg.id)}
                />
              ))
            )}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 border-t border-bg-border/40 shrink-0">
            <span className="text-2xs font-mono text-txt-muted">
              Order qty = lots × lot size · Lot size is set in the header bar above
            </span>
          </div>
        </div>

        {/* RIGHT — Analysis */}
        <div className="lg:w-[42%] xl:w-[40%] shrink-0 panel flex flex-col">

          {/* Summary cards 2×2 */}
          <div className="p-3 grid grid-cols-2 gap-2 border-b border-bg-border/60 shrink-0">
            <SummaryCard
              label="Max Profit"
              value={summary.maxProfit}
              valueClass={
                summary.maxProfit === 'Potentially Unlimited' ? 'text-bull' :
                summary.maxProfit === '—' ? 'text-txt-muted' : 'text-bull'
              }
            />
            <SummaryCard
              label="Max Loss"
              value={summary.maxLoss}
              valueClass={
                summary.maxLoss === 'Potentially Unlimited' ? 'text-bear' :
                summary.maxLoss === '—' ? 'text-txt-muted' : 'text-bear'
              }
            />
            <SummaryCard label="Breakeven" value={summary.breakeven} />
            <SummaryCard
              label="Net Premium"
              value={summary.netPremium}
              valueClass={netPremiumClass}
            />
          </div>

          {/* Tab bar */}
          <div className="flex items-center border-b border-bg-border shrink-0 px-1">
            {[
              { key: 'payoff',    label: 'Payoff' },
              { key: 'pl',        label: 'P&L' },
              { key: 'risk',      label: 'Risk' },
              { key: 'execution', label: 'Execution' },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={cn(
                  'px-3 py-2.5 text-xs font-mono font-medium border-b-2 transition-colors',
                  activeTab === key
                    ? 'border-amber text-amber'
                    : 'border-transparent text-txt-muted hover:text-txt-secondary'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'payoff' ? (
            <div className="flex-1 min-h-[240px] p-2">
              <PayoffChart
                series={payoff?.series ?? []}
                spotPrice={underlyingInfo.spot}
                breakevenPrices={payoff?.breakevenPrices ?? []}
              />
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 p-5 min-h-[200px]">
              <BarChart2 size={26} className="text-txt-muted/25" />
              <div className="text-center">
                <div className="text-xs font-semibold text-txt-secondary">
                  {activeTab === 'pl'   ? 'P&L Analysis' :
                   activeTab === 'risk' ? 'Risk Metrics' : 'Execution Plan'}
                </div>
                <div className="text-2xs font-mono text-txt-muted mt-0.5">Coming Soon</div>
              </div>
            </div>
          )}

          {/* Margin placeholder */}
          <div className="px-4 py-3 border-t border-bg-border/60 shrink-0 flex items-center justify-between">
            <span className="field-label">Margin Required</span>
            <span className="text-xs font-mono text-txt-muted">— (not computed)</span>
          </div>
        </div>
      </div>

      {/* ── 4. Sticky Action Bar ── */}
      <div className="sticky bottom-0 z-20 -mx-3 md:-mx-5 bg-bg-panel border-t border-bg-border px-4 py-3 flex items-center justify-between gap-3">
        <span className="text-2xs font-mono text-txt-muted hidden sm:inline">
          Draft · Local Preview · No data is persisted
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => toast.info('Draft saved locally — no backend call')}
            className="btn-ghost text-xs"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={handleSaveStrategy}
            disabled={isSaving}
            className="btn-secondary text-xs"
          >
            {isSaving ? 'Saving…' : 'Save Strategy'}
          </button>
          <button
            type="button"
            onClick={() => toast.info('Paper test coming in Phase 3')}
            className="btn-outline text-xs"
          >
            Paper Test
          </button>
          <button
            type="button"
            onClick={handleDeploy}
            disabled={isDeploying}
            className="btn-primary text-xs"
          >
            <Rocket size={12} />
            {isDeploying ? 'Saving…' : 'Deploy'}
          </button>
        </div>
      </div>

    </div>
  );
}
